// Copyright (c) Microsoft. All rights reserved.

import { Body1, Spinner, Title3 } from '@fluentui/react-components';
import { FC, useEffect } from 'react';
import { useSharedClasses } from '../../styles';
import { useTranslation } from 'react-i18next';

interface IData {
    uri: string;
    onBackendFound: () => void;
}

export const BackendProbe: FC<IData> = ({ uri, onBackendFound }) => {
    const classes = useSharedClasses();
    useEffect(() => {
        const timer = setInterval(() => {
            let requestUrl: URL;
            try {
                requestUrl = new URL('healthz', uri);
            } catch {
                // uri is unset/invalid (e.g. REACT_APP_BACKEND_URI missing) — nothing to probe.
                // Bail quietly instead of throwing an unhandled TypeError out of the interval every 3s.
                return;
            }

            // Defense-in-depth (SSRF hardening): the backend URI is build-time config, but
            // constrain the probe anyway — only http(s), and never follow a redirect that
            // could bounce the request to an unintended/internal host.
            if (requestUrl.protocol !== 'https:' && requestUrl.protocol !== 'http:') {
                return;
            }

            const fetchAsync = async () => {
                const result = await fetch(requestUrl, { redirect: 'error' });

                if (result.ok) {
                    onBackendFound();
                }
            };

            fetchAsync().catch(() => {
                // Ignore - this page is just a probe, so we don't need to show any errors if backend is not found
            });
        }, 3000);

        return () => {
            clearInterval(timer);
        };
    }, [uri, onBackendFound]);

    const { t } = useTranslation();

    if (process.env.NODE_ENV === 'development')
        return (
            <div className={classes.informativeView}>
                <Title3>Looking for your backend</Title3>
                <Spinner />
                <Body1>
                    The frontend expects to be able to reach the <strong>webapi/</strong> running at{' '}
                    <strong>{uri}</strong>
                </Body1>
                <Body1>
                    Run webapi using Visual Studio, Visual Studio Code or by typing the following command:{' '}
                    <strong>dotnet run</strong>, or by running the Start-backend script in the scripts folder.
                </Body1>
            </div>
        );

    return (
        <div className={classes.informativeView}>
            <Title3>{t('backendProbe.connectingToAi')}</Title3>
            <Spinner />
        </div>
    );
};
